"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";
import type { NakshatraId } from "@ishvaram/domain-data/nakshatras";

import { FINDER_FORM, FINDER_FRAME } from "./_finder-form";

// The /nakshatra/<slug>/ finder boundary (the #2460 pattern): the server page
// imports only this module, and it owns the one dynamic() to the island. The
// form, place search, kundali client, domain data and verdict load as an async
// chunk; the first load gains only this file, the shell and the hook.
//
// Activation is element-scoped (the dosha embeds' rule, 2026-10-05): a reader
// scrolling the article never downloads the island, and the deploy's body
// pointerdown smoke loads nothing; a tap, focus or submit on the shell does.
//
// The slot reserves the tallest state an engine reply can start from or land
// in, and every state fills it, so neither shell -> form nor a slow reply ->
// verdict (or error) moves the page below. Natural heights re-measured
// 2026-10-06 (next dev + Playwright, longest names, /kundali mocked) at 320 /
// 360 / 640+ wide: the form with an API error (and the saved-place chip or the
// place hint) is the tallest, 586 / 564 / 474 px; the longest verdict (three
// day windows) is 527 / 445 / 422; the shell 435 / 435 / 365. Hence 600 px on
// phones and 500 px from the sm breakpoint.
//
// The finder's own "Don't know your birth time?" explainer adds 97 to 143 px
// (up to 698 / 661 / 617 with an error showing), more than either floor
// holds. It opens only on the visitor's own press, a shift the input window
// excludes, and the form folds it on submit and keeps it shut until the reply
// lands (collapseTimeUnknownOnSubmit in NakshatraFinder), so no reply meets
// it. A layout-shift observer with it open into every verdict and the error,
// at 320 / 360 / 640 / 1280, counted no shift outside the input window.
// translate="no" covers the shell, form and verdict, which re-render on "Check
// another birth date" (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...FINDER_FORM, className: FINDER_FRAME } as const;

const NakshatraFinder = dynamic(() => import("./NakshatraFinder"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const FORM_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: "birthDate-default",
  tob: "birthTime-default",
  place: "birthPlace-default",
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && FORM_FIELD_IDS[name]) || FORM_FIELD_IDS.dob;
}

export default function NakshatraFinderLazy({
  pageNakshatraId,
  pagePadaNavamsha,
}: {
  pageNakshatraId: NakshatraId;
  pagePadaNavamsha: readonly string[];
}) {
  const { ready, activate } = useDeferredToolActivation({ scope: "element" });
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit on the shell; never after a touch, which may be
  // the start of a scroll.
  const shellIntent = ready
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(FORM_FIELD_IDS.dob),
      };

  return (
    <div
      translate="no"
      data-nakshatra-finder-slot=""
      className="flex min-h-[600px] flex-col sm:min-h-[500px]"
      {...shellIntent}
    >
      {ready ? (
        <NakshatraFinder
          pageNakshatraId={pageNakshatraId}
          pagePadaNavamsha={pagePadaNavamsha}
          focusFieldId={focusFieldId}
        />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={activate} />
      )}
    </div>
  );
}
